import { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { BusinessSubscription, SubscriptionInfo } from "lens-platform-sdk";
import { action } from "@k8slens/lds-icons";

import { getPlanName } from "src/services/plans";
import { openUrl } from "src/utils/url";
import { getAccountUrl } from "src/services/billing/billingUrl";
import type { ChildBusiness } from "src/hooks/useBusinessChildren";

import Table from "src/components/Table/Table";
import { ContextMenuItem } from "src/components/ContextMenu/ContextMenu";
import SubscriptionIdCell, { subscriptionIdColDef } from "src/components/TableCells/SubscriptionIdCell";
import { dateColDef } from "src/components/TableCells/DateCell";
import ContextMenuCell, { contextMenuColDef } from "src/components/TableCells/ContextMenuCell";

import styles from "./BillingDetails.module.css";

const subscriptionColumnHelper = createColumnHelper<SubscriptionInfo | BusinessSubscription>();
const { DeleteIcon } = action;

export interface SubscriptionsProps {
  subscriptions: SubscriptionInfo[] | BusinessSubscription[];
  businessChildren?: ChildBusiness[];
  billingPageToken: string | null;
  loading: boolean;
}

const isBusinessSubscription = (subscription: any): subscription is BusinessSubscription =>
  subscription.isBusinessAccount === true;

export const Subscriptions = ({
  subscriptions,
  billingPageToken: token,
  loading,
  businessChildren,
}: SubscriptionsProps) => {
  const [subscriptionsSorting, setSubscriptionsSorting] = useState<SortingState>([]);

  const cancelSubscription = useCallback(() => {
    if (!token) {
      return;
    }

    const accountUrl = getAccountUrl(token);

    if (!accountUrl) {
      return;
    }
    openUrl(accountUrl, true);
  }, [token]);

  /**
   * Check if there are any subscriptions that are from a child business
   */
  const someChildrenSubscriptions = useMemo(() => {
    return subscriptions.some((subscription) => {
      if (isBusinessSubscription(subscription) && typeof subscription.fromChildBusinessId === "string") {
        return true;
      }

      return false;
    });
  }, [subscriptions]);

  const subscriptionsColumns = useMemo(() => {
    const commonColumns = [
      subscriptionColumnHelper.accessor("id", {
        ...subscriptionIdColDef,
        cell: (props) => <SubscriptionIdCell className={styles.subscriptionId} {...props} />,
      }),
      subscriptionColumnHelper.accessor("planCode", {
        header: () => <span className="subscriptionsPlan">Plan</span>,
        cell: ({ getValue }) => <div className="subscriptionsPlanValue">{getPlanName(getValue())}</div>,
      }),
      subscriptionColumnHelper.accessor("currentPeriodEndsAt", {
        ...dateColDef,
        header: () => <span>Next Renewal Date</span>,
      }),
      subscriptionColumnHelper.accessor("seats", {
        header: () => <span>Quantity</span>,
        cell: ({ getValue }) => <div>{getValue()}</div>,
        meta: { primary: true },
      }),
      subscriptionColumnHelper.accessor("state", {
        header: () => <span>Status</span>,
        cell: ({ getValue }) => (
          <div className={clsx(styles.status, styles[getValue()] ?? styles.defaultState)}>{getValue()}</div>
        ),
      }),
    ];

    const contextMenuColumn = subscriptionColumnHelper.accessor("id", {
      ...contextMenuColDef,
      id: "actions",
      cell: ({ row }) => {
        const subscription = row.original;
        const fromChildBusinessId =
          isBusinessSubscription(subscription) && typeof subscription.fromChildBusinessId === "string";

        return (
          <ContextMenuCell toggleButtonProps={{ title: "Toggle subscription context menu" }}>
            <ContextMenuItem
              disabled={subscription.state === "canceled" || fromChildBusinessId}
              label="Cancel"
              icon={DeleteIcon}
              onClick={() => cancelSubscription()}
            />
          </ContextMenuCell>
        );
      },
    });

    if (someChildrenSubscriptions) {
      const fromChildBusinessIdColumn = subscriptionColumnHelper.accessor("fromChildBusinessId", {
        header: () => <span className="subscriptionsPlan">From Child Business</span>,
        cell: ({ getValue }) => {
          // assert as react-table (or TS?) doesn't know how to infer Unions
          const fromChildBusinessId = getValue() as BusinessSubscription["fromChildBusinessId"];
          const childBusinessName = businessChildren?.find((child) => child.id === fromChildBusinessId)?.name;

          return <span>{childBusinessName ?? ""}</span>;
        },
      });

      return [...commonColumns, fromChildBusinessIdColumn, contextMenuColumn];
    }

    return [...commonColumns, contextMenuColumn];
  }, [cancelSubscription, someChildrenSubscriptions, businessChildren]);

  const subscriptionsTable = useReactTable({
    data: subscriptions,
    columns: subscriptionsColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSubscriptionsSorting,
    state: {
      sorting: subscriptionsSorting,
    },
  });
  const subscriptionsTableRowModel = subscriptionsTable.getRowModel();
  const subscriptionsTableRows = useMemo(() => subscriptionsTableRowModel.rows, [subscriptionsTableRowModel]);

  return (
    <Table<BusinessSubscription | SubscriptionInfo>
      caption="Subscriptions"
      rows={subscriptionsTableRows}
      columns={subscriptionsTable.getAllColumns()}
      headerGroups={subscriptionsTable.getHeaderGroups()}
      noDataText="No subscriptions founded"
      loading={loading}
      headerGroupProps={{
        cellClassName: styles.tableHeader,
      }}
      rowProps={{
        cellClassName: styles.tableCell,
      }}
    />
  );
};
